/**
 * Filters all booking dates by a specific date
 *
 * @param {Array} bookingDates
 * @param {Date} date
 *
 * @returns {Array}
 */
export const filterAllBookingDates = (bookingDates, date) => {
    return bookingDates.filter((booking) => {
        const bookingDate = new Date(booking.start);
        const selectDate = new Date(date);
        return bookingDate.getMonth() === selectDate.getMonth() && bookingDate.getDate() === selectDate.getDate();
    });
};
/**
 * Compare two dates by day, month and year, if they are the equal.
 *
 * @param {Date} date1
 * @param {Date} date2
 *
 * @returns {Boolean}
 */
export const areDatesEquals = (date1, date2) => {
    return date1.getTime() === date2.getTime();
};
/**
 * Get a label for a time slot
 *
 * @param start
 * @param end
 *
 * @returns {string}
 */
export const getLabelForTimeSlot = (start, end) => {
    const dateStart = new Date(start);
    const timeStart = dateStart.toLocaleTimeString('DK-dk', { hour: '2-digit', minute: '2-digit', timeZone: 'Etc/GMT-1' });
    const dateEnd = new Date(end);
    const timeEnd = dateEnd.toLocaleTimeString('DK-dk', { hour: '2-digit', minute: '2-digit', timeZone: 'Etc/GMT-1' });
    return `Kl. ${timeStart} - ${timeEnd}`;
};
/**
 * Checks if provided date is greater than today
 *
 * @param {Date} date
 *
 * @returns {Boolean}
 */
export function isDateGreaterThanToday(date) {
    const today = new Date();
    date.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return date.getTime() > today.getTime();
}
/**
 * Checks if provided date is greater than today and has booking tokens
 *
 * @param {Array} bookingDates
 * @param {Date} date
 *
 * @returns {Boolean}
 */
export function isBookingDateDisabled(bookingDates, date) {
    if (!isDateGreaterThanToday(date)) {
        return true;
    }
    const providedDayBookings = bookingDates.filter((bookingDate) => {
        const bookingStartDate = new Date(bookingDate.start);
        return bookingStartDate.getDate() === date.getDate() && bookingStartDate.getMonth() === date.getMonth() && bookingStartDate.getFullYear() === date.getFullYear() && ![0, 6].includes(date.getDay());
    });
    if (providedDayBookings.length === 0) {
        return true;
    }
    return false;
}
/**
 * Returns provided date month info
 *
 * @param {Date} date
 *
 * @returns {{ year: Int, month: Int, startDate: Int, endDate: Int }}
 */
export function getMonthInfoFromDate(date = new Date()) {
    const now = new Date();
    date.setHours(0, 0, 0, 0);
    let month = date.getMonth() + 1;
    let year = date.getFullYear();
    let startDate = month > now.getMonth() + 1 ? 1 : isDateGreaterThanToday(date) ? date.getDate() : date.getDate() + 1;
    let numberOfDaysInMonth = new Date(year, month, 0).getDate();
    if (startDate > numberOfDaysInMonth) {
        month += 1;
        if (month > 12) {
            month = 0;
            year += 1;
        }
        numberOfDaysInMonth = new Date(year, month, 0).getDate();
        startDate = 1;
    }
    return {
        month,
        year,
        startDate,
        endDate: numberOfDaysInMonth
    };
}
/**
 * Returns next month days info
 *
 * @param {Int} month
 * @param {Int} year
 *
 * @returns {{ month: Int, year: Int }}
 */
export function getNextMonthDaysInfo(month, year) {
    let nextMonth = month + 1;
    let newYear = year;
    let newEndDate = new Date(year, nextMonth - 1, 0).getDate();
    if (nextMonth > 12) {
        newYear += 1;
        nextMonth = 1;
        newEndDate = new Date(newYear, nextMonth - 1, 0).getDate();
    }
    return {
        month: nextMonth,
        year: newYear,
        startDate: 1,
        endDate: newEndDate
    };
}
/**
 * Returns provided month disabled days
 *
 * @param {Array} bookings
 * @param {Int} month
 * @param {Int} year
 *
 * @returns {Array}
 */
export function getMonthDisabledDays(bookings, month, year) {
    const disabledDays = [];
    const numberOfDaysInMonth = new Date(year, month, 0).getDate();
    for (let day = 1; day <= numberOfDaysInMonth; day++) {
        const date = new Date(year, month - 1, day);
        if (isBookingDateDisabled(bookings, date)) {
            disabledDays.push(date);
        }
    }
    return disabledDays;
}
/**
 * Returns first weekday time slot which is greater than today
 *
 * @param {Array} bookings
 *
 * @returns {Date}
 */
export function getWeekDayTimeSlotDate(bookings) {
    let weekDayTimeSlot = null;
    bookings.forEach((timeSlot) => {
        const timesSlotStartDate = new Date(timeSlot.start);
        if (weekDayTimeSlot == null && ![0, 6].includes(timesSlotStartDate.getDay()) && isDateGreaterThanToday(timesSlotStartDate)) {
            weekDayTimeSlot = new Date(timeSlot.start);
        }
    });
    return weekDayTimeSlot !== null && weekDayTimeSlot !== void 0 ? weekDayTimeSlot : new Date();
}
